import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { ThemeToggle } from '@/components/ThemeToggle';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { LocationPicker } from '@/components/LocationPicker';
import { Fuel, Truck, ShieldCheck, Loader2, Building2, MapPinned, MessageCircle, Plus, Trash2, ArrowLeft, FileCheck2, Upload, X, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';

const MAX_DOC_KB = 300;

const HOME_BY_ROLE = { admin: '/admin', driver: '/driver', pump: '/pump' };
const routeFor = (u) => HOME_BY_ROLE[u.role] || '/app';

const addrUid = () => `loc-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
const newAddr = () => ({ _uid: addrUid(), label: '', address: '', lat: 0, lng: 0 });

// Label for send/resend code buttons (flat logic instead of nested ternaries)
const resendLabel = (sent, secondsLeft, sendText, resendText) => {
  if (!sent) return sendText;
  if (secondsLeft > 0) return `Resend in ${secondsLeft}s`;
  return resendText;
};

export default function SignIn() {
  const { login, register, loginWithData } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  // register
  const [reg, setReg] = useState({ name: '', email: '', password: '', phone: '', company_name: '', gstin: '', pan: '', gst_doc: null, gst_doc_name: '', pan_doc: null, pan_doc_name: '', customer_type: 'Commercial', billing_company_id: 'mfk', payment_terms: 'Net 15', billing_address: '' });

  // KYC document pick: enforce the 300KB size limit client-side, store as base64 data URL.
  const pickDoc = (field) => (e) => {
    const f = e.target.files?.[0];
    e.target.value = '';
    if (!f) return;
    if (f.size > MAX_DOC_KB * 1024) {
      toast.error(`${f.name} is ${Math.round(f.size / 1024)}KB — maximum allowed size is ${MAX_DOC_KB}KB`);
      return;
    }
    const reader = new FileReader();
    reader.onload = () => setReg((r) => ({ ...r, [field]: reader.result, [`${field}_name`]: f.name }));
    reader.onerror = () => toast.error('Could not read the file. Please try again.');
    reader.readAsDataURL(f);
  };
  const clearDoc = (field) => setReg((r) => ({ ...r, [field]: null, [`${field}_name`]: '' }));
  const [companies, setCompanies] = useState([]);
  // One company can register multiple delivery locations.
  const [deliveryAddrs, setDeliveryAddrs] = useState([newAddr()]);
  const addLocation = () => setDeliveryAddrs((list) => [...list, newAddr()]);
  const removeLocation = (idx) => setDeliveryAddrs((list) => (list.length > 1 ? list.filter((_, i) => i !== idx) : list));
  const updateLocation = (idx, patch) => setDeliveryAddrs((list) => list.map((a, i) => (i === idx ? { ...a, ...patch } : a)));
  // Public list for registration — display labels per business preference.
  const COMPANY_LABELS = { srps: 'Shaheed Ramendra Pratap Singh Filling Station', mfk: 'M Fuel Kart Pvt Ltd' };
  useEffect(() => { api.get('/billing-companies/public').then((r) => setCompanies(r.data)).catch(() => setCompanies([{ id: 'srps' }, { id: 'mfk' }])); }, []);
  // otp
  const [phone, setPhone] = useState('');
  const [otp, setOtp] = useState('');
  const [devOtp, setDevOtp] = useState(null);
  const [otpSent, setOtpSent] = useState(false);
  const [resendIn, setResendIn] = useState(0);   // seconds until a new code can be requested
  const [expiresIn, setExpiresIn] = useState(0); // seconds until the current code expires

  // Tick down the resend cooldown and expiry countdown once per second.
  useEffect(() => {
    if (resendIn <= 0 && expiresIn <= 0) return undefined;
    const t = setInterval(() => {
      setResendIn((s) => (s > 0 ? s - 1 : 0));
      setExpiresIn((s) => (s > 0 ? s - 1 : 0));
    }, 1000);
    return () => clearInterval(t);
  }, [resendIn, expiresIn]);

  const mmss = (s) => `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;

  // Forgot password (email + 6-digit code)
  const [forgotOpen, setForgotOpen] = useState(false);
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotSent, setForgotSent] = useState(false);
  const [forgotCode, setForgotCode] = useState('');
  const [forgotNewPass, setForgotNewPass] = useState('');
  const [forgotDevCode, setForgotDevCode] = useState(null);
  const [forgotResendIn, setForgotResendIn] = useState(0);

  useEffect(() => {
    if (forgotResendIn <= 0) return undefined;
    const t = setInterval(() => setForgotResendIn((s) => (s > 0 ? s - 1 : 0)), 1000);
    return () => clearInterval(t);
  }, [forgotResendIn]);

  const sendResetCode = async () => {
    const em = forgotEmail.trim();
    if (!em) return toast.error('Enter your email ID');
    setBusy(true);
    try {
      const r = await api.post('/auth/forgot/start', { email: em });
      setForgotSent(true);
      setForgotResendIn(r.data.resend_after || 60);
      setForgotDevCode(r.data.dev_code || null);
      toast.success(r.data.channel === 'email' ? 'Reset code sent to your email' : 'Reset code generated');
    } catch (err) { toast.error(err.response?.data?.detail || 'Could not send the code'); }
    finally { setBusy(false); }
  };

  const doResetPassword = async () => {
    setBusy(true);
    try {
      await api.post('/auth/forgot/verify', { email: forgotEmail.trim(), code: forgotCode, new_password: forgotNewPass });
      toast.success('Password updated! Sign in with your new password.');
      setForgotOpen(false); setForgotSent(false); setForgotCode(''); setForgotNewPass(''); setForgotDevCode(null);
      setEmail(forgotEmail.trim()); setPassword('');
    } catch (err) { toast.error(err.response?.data?.detail || 'Reset failed'); }
    finally { setBusy(false); }
  };

  const doGoogleSignIn = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    // Path-agnostic: the global hash interceptor in App.js renders AuthCallback for any
    // route whose hash contains #session_id, so the origin root works in every environment.
    const redirectUrl = window.location.origin;
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const doLogin = async (e) => {
    e && e.preventDefault();
    setBusy(true);
    try {
      const u = await login(email.trim(), password);
      toast.success(`Welcome back, ${u.name}`);
      nav(routeFor(u));
    } catch (err) { toast.error(err.response?.data?.detail || 'Login failed'); }
    finally { setBusy(false); }
  };

  const doRegister = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const delivery_addresses = deliveryAddrs
        .filter((a) => a.address && a.address.trim())
        .map((a, i) => ({ label: a.label || `Location ${i + 1}`, address: a.address, lat: a.lat || null, lng: a.lng || null }));
      const u = await register({ ...reg, delivery_addresses });
      toast.success('Account created!');
      nav(routeFor(u));
    } catch (err) { toast.error(err.response?.data?.detail || 'Registration failed'); }
    finally { setBusy(false); }
  };

  const sendOtp = async () => {
    if (!phone) return toast.error('Enter your WhatsApp number');
    if (resendIn > 0) return;
    setBusy(true);
    try {
      const r = await api.post('/auth/otp/start', { phone });
      setDevOtp(r.data.dev_otp || null);
      setOtpSent(true);
      setResendIn(r.data.resend_after || 30);
      setExpiresIn(r.data.expires_in || 300);
      setOtp('');
      toast.success(r.data.channel === 'whatsapp' ? 'OTP sent to your WhatsApp' : 'OTP generated (dev mode)');
    }
    catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
    finally { setBusy(false); }
  };
  const verifyOtp = async () => {
    if (expiresIn <= 0) return toast.error('Code expired. Please resend a new OTP.');
    setBusy(true);
    try {
      const r = await api.post('/auth/otp/verify', { phone, otp });
      const u = loginWithData(r.data);
      toast.success('Logged in');
      nav(routeFor(u));
    }
    catch (err) {
      const detail = err.response?.data?.detail || 'Invalid OTP';
      if (/expired|too many|no active/i.test(detail)) { setExpiresIn(0); setDevOtp(null); }
      toast.error(detail);
    }
    finally { setBusy(false); }
  };

  return (
    <div className="mfk-theme min-h-screen bg-background text-foreground">
      <header className="sticky top-0 z-40 border-b border-border bg-background/85 backdrop-blur-md">
        <div className="mx-auto max-w-7xl px-4 h-16 flex items-center justify-between gap-4">
          <Link to="/login" className="flex items-center gap-2 shrink-0" data-testid="signin-logo">
            <span className="bg-white rounded-lg px-2.5 py-1.5 shadow-sm"><img src="/assets/logo.png" alt="Mobile Fuel Kart" className="h-6" /></span>
          </Link>
          <div className="flex items-center gap-2">
            <ThemeToggle />
            <Link to="/login"><Button size="sm" variant="secondary" data-testid="signin-back-button"><ArrowLeft className="h-4 w-4 mr-1.5" />Back to Home</Button></Link>
          </div>
        </div>
      </header>

      <div className="mx-auto max-w-7xl px-4 py-10 sm:py-14">
        <div className="grid gap-10 lg:grid-cols-[minmax(0,1fr)_minmax(0,28rem)] lg:items-stretch">
          {/* Cinematic brand panel (desktop) */}
          <div className="relative hidden lg:flex flex-col justify-between overflow-hidden rounded-3xl border border-border lux-noise p-10"
            style={{ background: 'linear-gradient(135deg, hsl(var(--lux-teal-deep)), hsl(var(--primary)))' }}
            aria-hidden="true" data-testid="signin-brand-panel">
            <div className="absolute inset-0 pointer-events-none lux-grid-overlay opacity-40" />
            <div className="absolute -top-16 right-10 h-56 w-56 rounded-full bg-white/10 blur-3xl lux-floaty pointer-events-none" />
            <div className="absolute bottom-10 left-1/3 h-40 w-40 rounded-full bg-accent/20 blur-3xl lux-floaty-slow pointer-events-none" />
            <div className="relative">
              <span className="inline-flex bg-white rounded-xl px-3 py-2 shadow-lg"><img src="/assets/logo.png" alt="Mobile Fuel Kart" className="h-7" /></span>
              <h1 className="mt-10 font-luxe text-4xl font-semibold leading-tight text-white">Fuel, delivered with<br />precision & trust.</h1>
              <p className="mt-4 text-white/75 max-w-md text-sm leading-relaxed">One account for ordering diesel, tracking live deliveries, invoices and daily pump operations — engineered for Indian businesses.</p>
            </div>
            <div className="relative grid grid-cols-3 gap-3">
              {[['24/7', 'Emergency response'], ['PESO', 'Licensed fleet'], ['₹/L', 'Transparent pricing']].map(([v, l]) => (
                <div key={l} className="rounded-2xl bg-white/10 backdrop-blur-md border border-white/15 px-4 py-3">
                  <p className="font-display text-xl font-semibold text-white tabular">{v}</p>
                  <p className="text-[11px] text-white/70 mt-0.5">{l}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Auth card */}
          <div className="w-full max-w-md mx-auto lg:mx-0">
        <div id="auth-card" className="rounded-2xl border border-border bg-card/90 backdrop-blur-xl p-5 sm:p-6 shadow-[0_18px_60px_-30px_rgba(15,23,42,0.45)]">
          <h2 className="font-luxe text-2xl font-semibold">Welcome</h2>
          <p className="text-sm text-muted-foreground mb-5">Sign in to your account</p>

          <Button type="button" variant="outline" className="w-full h-11 font-medium" onClick={doGoogleSignIn} data-testid="google-signin-button">
            <svg className="h-4 w-4 mr-2" viewBox="0 0 24 24" aria-hidden="true">
              <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.27-4.74 3.27-8.1z" />
              <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84A11 11 0 0 0 12 23z" />
              <path fill="#FBBC05" d="M5.84 14.1a6.6 6.6 0 0 1 0-4.2V7.06H2.18a11 11 0 0 0 0 9.88l3.66-2.84z" />
              <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15A11 11 0 0 0 2.18 7.06l3.66 2.84c.87-2.6 3.3-4.52 6.16-4.52z" />
            </svg>
            Continue with Google
          </Button>
          <div className="flex items-center gap-3 my-4" aria-hidden="true">
            <span className="h-px flex-1 bg-border" /><span className="text-xs text-muted-foreground">or</span><span className="h-px flex-1 bg-border" />
          </div>

          <Tabs defaultValue="login">
            <TabsList className="grid grid-cols-3 w-full">
              <TabsTrigger value="login" data-testid="tab-login">Login</TabsTrigger>
              <TabsTrigger value="otp" data-testid="tab-otp"><MessageCircle className="h-3.5 w-3.5 mr-1" />WhatsApp OTP</TabsTrigger>
              <TabsTrigger value="register" data-testid="tab-register">Register</TabsTrigger>
            </TabsList>

            <TabsContent value="login">
              {!forgotOpen ? (
              <form onSubmit={doLogin} className="space-y-3 mt-4">
                <div><Label>Email</Label><Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@company.com" required data-testid="login-email-input" /></div>
                <div><Label>Password</Label><Input type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="••••••••" required data-testid="login-password-input" /></div>
                <Button type="submit" className="w-full h-11" disabled={busy} data-testid="login-submit-button">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Sign In'}</Button>
                <button type="button" className="w-full text-center text-sm text-primary font-medium hover:underline" onClick={() => { setForgotOpen(true); setForgotEmail(email); }} data-testid="forgot-password-link">Forgot password?</button>
              </form>
              ) : (
              <div className="space-y-3 mt-4" data-testid="forgot-password-panel">
                <div>
                  <h3 className="text-sm font-semibold">Reset your password</h3>
                  <p className="text-xs text-muted-foreground">We'll email a 6-digit code to your registered email ID.</p>
                </div>
                <div className="flex gap-2">
                  <div className="flex-1"><Label>Email</Label><Input type="email" value={forgotEmail} onChange={(e) => setForgotEmail(e.target.value)} placeholder="you@company.com" data-testid="forgot-email-input" /></div>
                  <Button type="button" className="self-end h-10" onClick={sendResetCode} disabled={busy || forgotResendIn > 0} data-testid="forgot-send-button">
                    {resendLabel(forgotSent, forgotResendIn, 'Send code', 'Resend code')}
                  </Button>
                </div>
                {forgotDevCode && <div className="rounded-lg bg-muted p-3 text-sm" data-testid="forgot-dev-code">Dev code: <b>{forgotDevCode}</b> (real email once SendGrid/Gmail SMTP is configured)</div>}
                {forgotSent && (
                  <>
                    <div><Label>6-digit code</Label><Input inputMode="numeric" maxLength={6} value={forgotCode} onChange={(e) => setForgotCode(e.target.value.replace(/\D/g, ''))} placeholder="123456" data-testid="forgot-code-input" /></div>
                    <div><Label>New password</Label><Input type="password" value={forgotNewPass} onChange={(e) => setForgotNewPass(e.target.value)} placeholder="Min 6 characters" data-testid="forgot-new-password-input" /></div>
                    <Button type="button" className="w-full h-11" onClick={doResetPassword} disabled={busy || forgotCode.length !== 6 || forgotNewPass.length < 6} data-testid="forgot-reset-button">
                      {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Reset Password'}
                    </Button>
                  </>
                )}
                <button type="button" className="w-full text-center text-sm text-muted-foreground hover:text-foreground" onClick={() => setForgotOpen(false)} data-testid="forgot-back-link">Back to sign in</button>
              </div>
              )}
            </TabsContent>

            <TabsContent value="otp">
              <div className="space-y-3 mt-4">
                <div className="flex gap-2"><div className="flex-1"><Label>WhatsApp Number</Label><Input value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="9821000001" data-testid="otp-phone-input" /></div>
                  <Button type="button" className="self-end h-10" onClick={sendOtp} disabled={busy || resendIn > 0} data-testid="otp-send-button">
                    <MessageCircle className="h-4 w-4 mr-1" />{resendLabel(otpSent, resendIn, 'Send OTP', 'Resend OTP')}
                  </Button>
                </div>
                {devOtp && expiresIn > 0 && <div className="rounded-lg bg-muted p-3 text-sm" data-testid="otp-dev-code">Dev OTP: <b>{devOtp}</b> (real WhatsApp via Twilio when configured)</div>}
                {otpSent && (
                  expiresIn > 0
                    ? <p className="text-xs text-muted-foreground" data-testid="otp-expiry-timer">Code expires in <b className="tabular text-foreground">{mmss(expiresIn)}</b></p>
                    : <p className="text-xs text-destructive" data-testid="otp-expired-msg">Code expired — please resend a new OTP.</p>
                )}
                <div><Label>Enter OTP</Label><Input value={otp} onChange={(e) => setOtp(e.target.value)} placeholder="6-digit code" data-testid="otp-code-input" /></div>
                <Button type="button" className="w-full h-11" onClick={verifyOtp} disabled={busy || !otpSent || expiresIn <= 0} data-testid="otp-verify-button">Verify &amp; Login</Button>
                <p className="text-xs text-muted-foreground">We&apos;ll send a 6-digit code to your WhatsApp. Tip: use seeded number 9821000001 (customer).</p>
              </div>
            </TabsContent>

            <TabsContent value="register">
              <form onSubmit={doRegister} className="space-y-3 mt-4">
                <div className="grid grid-cols-2 gap-3">
                  <div><Label>Contact Name</Label><Input value={reg.name} onChange={(e) => setReg({ ...reg, name: e.target.value })} required data-testid="reg-name-input" /></div>
                  <div><Label>Phone</Label><Input value={reg.phone} onChange={(e) => setReg({ ...reg, phone: e.target.value })} data-testid="reg-phone-input" /></div>
                </div>
                <div><Label>Company Name</Label><Input value={reg.company_name} onChange={(e) => setReg({ ...reg, company_name: e.target.value })} required data-testid="reg-company-input" /></div>
                <div><Label>Email</Label><Input type="email" value={reg.email} onChange={(e) => setReg({ ...reg, email: e.target.value })} required data-testid="reg-email-input" /></div>
                <div><Label>Password</Label><Input type="password" value={reg.password} onChange={(e) => setReg({ ...reg, password: e.target.value })} required data-testid="reg-password-input" /></div>

                {/* KYC: GST + PAN numbers with document uploads (max 300KB each) */}
                <div className="rounded-xl border border-border bg-muted/30 p-3 space-y-3" data-testid="reg-kyc-section">
                  <Label className="flex items-center gap-1.5"><FileCheck2 className="h-4 w-4 text-primary" />KYC Details</Label>
                  <div className="grid grid-cols-2 gap-3">
                    <div><Label className="text-xs">GST No</Label><Input value={reg.gstin} onChange={(e) => setReg({ ...reg, gstin: e.target.value.toUpperCase() })} placeholder="e.g. 09AAECE4908N1Z8" data-testid="reg-gstin-input" /></div>
                    <div><Label className="text-xs">PAN No</Label><Input value={reg.pan} onChange={(e) => setReg({ ...reg, pan: e.target.value.toUpperCase() })} placeholder="e.g. AAECE4908N" data-testid="reg-pan-input" /></div>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    {[{ field: 'gst_doc', label: 'GST Certificate', tid: 'gst' }, { field: 'pan_doc', label: 'PAN Card', tid: 'pan' }].map((d) => (
                      <div key={d.field}>
                        <Label className="text-xs">{d.label}</Label>
                        {reg[`${d.field}_name`] ? (
                          <div className="flex items-center gap-1.5 rounded-lg border border-border bg-card px-2.5 h-10 text-xs" data-testid={`reg-${d.tid}-doc-selected`}>
                            <CheckCircle2 className="h-3.5 w-3.5 text-emerald-600 shrink-0" />
                            <span className="truncate flex-1">{reg[`${d.field}_name`]}</span>
                            <button type="button" onClick={() => clearDoc(d.field)} className="p-0.5 rounded hover:bg-muted" aria-label={`Remove ${d.label}`} data-testid={`reg-${d.tid}-doc-remove`}><X className="h-3.5 w-3.5 text-muted-foreground" /></button>
                          </div>
                        ) : (
                          <label className="flex items-center justify-center gap-1.5 rounded-lg border border-dashed border-border bg-card h-10 text-xs text-muted-foreground cursor-pointer hover:bg-muted/60 hover:text-foreground transition-colors">
                            <Upload className="h-3.5 w-3.5" />Upload
                            <input type="file" accept=".pdf,.jpg,.jpeg,.png" className="hidden" onChange={pickDoc(d.field)} data-testid={`reg-${d.tid}-doc-input`} />
                          </label>
                        )}
                      </div>
                    ))}
                  </div>
                  <p className="text-xs text-muted-foreground">PDF / JPG / PNG — maximum {MAX_DOC_KB}KB per document.</p>
                </div>
                <div><Label>Bill under company</Label>
                  <Select value={reg.billing_company_id} onValueChange={(v) => setReg({ ...reg, billing_company_id: v })}>
                    <SelectTrigger data-testid="reg-billing-company-select"><SelectValue placeholder="Select billing company" /></SelectTrigger>
                    <SelectContent>{companies.map((co) => <SelectItem key={co.id} value={co.id}>{COMPANY_LABELS[co.id] || co.name}</SelectItem>)}</SelectContent>
                  </Select>
                  <p className="text-xs text-muted-foreground mt-1">Your invoices will be issued by this company.</p>
                </div>
                <div><Label>Credit Period (payment terms)</Label>
                  <div className="flex items-center h-10 rounded-lg border border-border bg-muted/40 px-3 text-sm text-muted-foreground" data-testid="reg-credit-period-note">
                    Set by our team after account verification
                  </div>
                  <p className="text-xs text-muted-foreground mt-1">Credit period and credit limit are assigned by the admin (maximum 20 days credit).</p>
                </div>

                {/* Billing address */}
                <div>
                  <Label className="flex items-center gap-1.5"><Building2 className="h-3.5 w-3.5" />Billing Address</Label>
                  <Textarea value={reg.billing_address} onChange={(e) => setReg({ ...reg, billing_address: e.target.value })} placeholder="Registered office / GST billing address" rows={2} data-testid="reg-billing-address-input" />
                </div>

                {/* Multiple delivery locations */}
                <div className="rounded-xl border border-border bg-muted/30 p-3 space-y-3" data-testid="reg-delivery-section">
                  <div className="flex items-center justify-between">
                    <Label className="flex items-center gap-1.5 mb-0"><MapPinned className="h-4 w-4 text-primary" />Delivery Locations</Label>
                    <span className="text-xs text-muted-foreground">Add every site you want diesel delivered to</span>
                  </div>
                  {deliveryAddrs.map((a, idx) => (
                    <div key={a._uid} className="rounded-lg border border-border bg-card p-3 space-y-2" data-testid={`reg-delivery-item-${idx}`}>
                      <div className="flex items-center gap-2">
                        <Input
                          className="flex-1"
                          placeholder={`Location label (e.g. Plant ${idx + 1}, Warehouse)`}
                          value={a.label}
                          onChange={(e) => updateLocation(idx, { label: e.target.value })}
                          data-testid={`reg-delivery-label-${idx}`}
                        />
                        {deliveryAddrs.length > 1 && (
                          <Button type="button" variant="ghost" size="icon" onClick={() => removeLocation(idx)} data-testid={`reg-delivery-remove-${idx}`} aria-label="Remove location">
                            <Trash2 className="h-4 w-4 text-destructive" />
                          </Button>
                        )}
                      </div>
                      {/* 1) Pick the point on the map (search-based) */}
                      <LocationPicker
                        value={{ address: a.address, lat: a.lat, lng: a.lng }}
                        onChange={(next) => updateLocation(idx, next)}
                        testId={`reg-location-picker-${idx}`}
                      />
                      {/* 2) Full address text below the map */}
                      <div>
                        <Label className="text-xs text-muted-foreground">Full Address</Label>
                        <Textarea
                          rows={2}
                          placeholder="House/plot, street, area, city, pincode"
                          value={a.address}
                          onChange={(e) => updateLocation(idx, { address: e.target.value })}
                          data-testid={`reg-delivery-address-${idx}`}
                        />
                      </div>
                    </div>
                  ))}
                  <Button type="button" variant="outline" size="sm" className="w-full" onClick={addLocation} data-testid="reg-add-location-button">
                    <Plus className="h-4 w-4 mr-1" />Add another location
                  </Button>
                </div>
                <Button type="submit" className="w-full h-11" disabled={busy} data-testid="register-submit-button">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Create Business Account'}</Button>
              </form>
            </TabsContent>
          </Tabs>
        </div>
          </div>
        </div>
      </div>
    </div>
  );
}
